/**
 * 轻提示: toast("已保存") / toast.success(…) / toast.error(…) / toast.info(…)
 * 第二个参数可选 { description, duration }。<Toaster /> 在 App 根部挂一次。
 * 手机在顶部弹出 (避开底部 tab 栏), 桌面在右下角。
 */
import type { ReactNode } from "react";
import { create } from "zustand";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  title: ReactNode;
  description?: ReactNode;
}

interface ToastOptions {
  description?: ReactNode;
  /** 毫秒, 默认 3500 (错误 5000); 0 = 不自动消失 */
  duration?: number;
}

interface ToastState {
  toasts: ToastItem[];
  push: (t: Omit<ToastItem, "id">, duration: number) => number;
  dismiss: (id: number) => void;
}

let seq = 0;
const MAX = 4;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (t, duration) => {
    const id = ++seq;
    set({ toasts: [...get().toasts, { ...t, id }].slice(-MAX) });
    if (duration > 0) setTimeout(() => get().dismiss(id), duration);
    return id;
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

function show(kind: ToastKind, title: ReactNode, opts: ToastOptions = {}) {
  const duration = opts.duration ?? (kind === "error" ? 5000 : 3500);
  return useToastStore.getState().push({ kind, title, description: opts.description }, duration);
}

export const toast = Object.assign((title: ReactNode, opts?: ToastOptions) => show("info", title, opts), {
  success: (title: ReactNode, opts?: ToastOptions) => show("success", title, opts),
  error: (title: ReactNode, opts?: ToastOptions) => show("error", title, opts),
  info: (title: ReactNode, opts?: ToastOptions) => show("info", title, opts),
  dismiss: (id: number) => useToastStore.getState().dismiss(id),
});

const ICON = { success: CheckCircle2, error: AlertCircle, info: Info } as const;
const ICON_CLASS = { success: "text-success", error: "text-danger", info: "text-info" } as const;

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <div
      aria-live="polite"
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 p-3 pt-[max(0.75rem,env(safe-area-inset-top))]",
        "sm:inset-x-auto sm:bottom-0 sm:right-0 sm:top-auto sm:items-end sm:p-5",
      )}
    >
      {toasts.map((t) => {
        const Icon = ICON[t.kind];
        return (
          <div
            key={t.id}
            role={t.kind === "error" ? "alert" : "status"}
            className="pointer-events-auto flex w-full max-w-sm animate-toast-in items-start gap-2.5 rounded-xl border bg-card px-3.5 py-3 text-sm shadow-lg"
          >
            <Icon aria-hidden className={cn("mt-0.5 h-4 w-4 shrink-0", ICON_CLASS[t.kind])} />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-foreground">{t.title}</div>
              {t.description && <div className="mt-0.5 text-xs text-muted-foreground">{t.description}</div>}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="关闭提示"
              className="-m-1 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
